import type { Plugin, PluginContext } from 'aws-rum-web';

import {
  HTTP_CONTENT_EVENT_TYPE,
  fitEventPayload,
  isRumDataplaneUrl,
  sanitizeHeaders,
  serializeBody,
} from './contentCaptureUtils';

type XhrState = {
  method: string;
  url: string;
  headers: Record<string, string>;
  requestBody: string;
};

type FetchFn = typeof fetch;

const xhrStateByRequest = new WeakMap<XMLHttpRequest, XhrState>();

const readRequestBody = async (body: BodyInit | null | undefined): Promise<string> => {
  if (body == null) return '';

  if (typeof body === 'string') {
    return body;
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  if (body instanceof FormData) {
    const fields: Record<string, string> = {};
    body.forEach((value, key) => {
      fields[key] = typeof value === 'string' ? value : '[binary]';
    });
    return JSON.stringify(fields);
  }

  if (body instanceof Blob) {
    return body.type.includes('json') || body.type.includes('text')
      ? await body.text()
      : '[binary]';
  }

  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return '[binary]';
  }

  try {
    return await new Response(body).text();
  } catch {
    return '[unreadable request body]';
  }
};

const resolveFetchRequest = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<{
  method: string;
  url: string;
  headers: Record<string, string>;
  requestBody: string;
}> => {
  if (input instanceof Request) {
    const cloned = input.clone();
    const headers: Record<string, string> = {};
    cloned.headers.forEach((value, key) => {
      headers[key] = value;
    });

    return {
      method: cloned.method,
      url: cloned.url,
      headers: sanitizeHeaders(headers),
      requestBody: await readRequestBody(cloned.body),
    };
  }

  const headers: Record<string, string> = {};
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(init.headers)) {
      init.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, init.headers);
    }
  }

  return {
    method: init?.method ?? 'GET',
    url: String(input),
    headers: sanitizeHeaders(headers),
    requestBody: await readRequestBody(init?.body),
  };
};

const readFetchResponseBody = async (response: Response): Promise<string> => {
  const contentType = response.headers.get('content-type');
  if (
    contentType &&
    !contentType.includes('json') &&
    !contentType.includes('text') &&
    !contentType.includes('xml') &&
    !contentType.includes('javascript')
  ) {
    return `[${contentType}: not captured]`;
  }

  try {
    const cloned = response.clone();
    return await cloned.text();
  } catch {
    return '[failed to read response body]';
  }
};

const parseXhrResponseHeaders = (rawHeaders: string): Record<string, string> =>
  rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .filter(Boolean)
    .reduce<Record<string, string>>((headers, line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex === -1) return headers;
      const key = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1).trim();
      headers[key] = value;
      return headers;
    }, {});

export class RumNetworkContentPlugin implements Plugin {
  private context!: PluginContext;
  private enabled = false;
  private originalFetch: FetchFn | null = null;
  private originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXhrSend: typeof XMLHttpRequest.prototype.send | null = null;
  private originalXhrSetRequestHeader: typeof XMLHttpRequest.prototype.setRequestHeader | null =
    null;

  load(context: PluginContext): void {
    this.context = context;
    this.enable();
  }

  enable(): void {
    if (this.enabled || typeof window === 'undefined') return;
    this.enabled = true;
    this.patchFetch();
    this.patchXhr();
  }

  disable(): void {
    if (!this.enabled || typeof window === 'undefined') return;
    this.enabled = false;

    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }

    if (this.originalXhrOpen) {
      XMLHttpRequest.prototype.open = this.originalXhrOpen;
      this.originalXhrOpen = null;
    }
    if (this.originalXhrSend) {
      XMLHttpRequest.prototype.send = this.originalXhrSend;
      this.originalXhrSend = null;
    }
    if (this.originalXhrSetRequestHeader) {
      XMLHttpRequest.prototype.setRequestHeader = this.originalXhrSetRequestHeader;
      this.originalXhrSetRequestHeader = null;
    }
  }

  getPluginId(): string {
    return 'RumNetworkContentPlugin';
  }

  private recordHttpEvent(event: Record<string, unknown>): void {
    this.context.record(HTTP_CONTENT_EVENT_TYPE, fitEventPayload(event));
  }

  private patchFetch(): void {
    const recordHttpEvent = (event: Record<string, unknown>): void => {
      this.recordHttpEvent(event);
    };

    this.originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const request = await resolveFetchRequest(input, init);
      if (isRumDataplaneUrl(request.url)) {
        return this.originalFetch!(input, init);
      }

      const startedAt = performance.now();

      try {
        const response = await this.originalFetch!(input, init);
        const responseBody = await readFetchResponseBody(response);
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });

        recordHttpEvent({
          transport: 'fetch',
          method: request.method,
          url: request.url,
          status: response.status,
          durationMs: Math.round(performance.now() - startedAt),
          requestHeaders: request.headers,
          responseHeaders: sanitizeHeaders(responseHeaders),
          requestBody: serializeBody(request.requestBody),
          responseBody: serializeBody(responseBody, response.headers.get('content-type')),
        });

        return response;
      } catch (error) {
        recordHttpEvent({
          transport: 'fetch',
          method: request.method,
          url: request.url,
          status: 0,
          durationMs: Math.round(performance.now() - startedAt),
          requestHeaders: request.headers,
          responseHeaders: {},
          requestBody: serializeBody(request.requestBody),
          responseBody: '',
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    };
  }

  private patchXhr(): void {
    const recordHttpEvent = (event: Record<string, unknown>): void => {
      this.recordHttpEvent(event);
    };

    this.originalXhrOpen = XMLHttpRequest.prototype.open;
    this.originalXhrSend = XMLHttpRequest.prototype.send;
    this.originalXhrSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    const originalOpen = this.originalXhrOpen;
    const originalSend = this.originalXhrSend;
    const originalSetRequestHeader = this.originalXhrSetRequestHeader;

    XMLHttpRequest.prototype.open = function open(
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null
    ): void {
      xhrStateByRequest.set(this, {
        method,
        url: String(url),
        headers: {},
        requestBody: '',
      });
      return originalOpen.call(this, method, url, async ?? true, username, password);
    };

    XMLHttpRequest.prototype.setRequestHeader = function setRequestHeader(
      name: string,
      value: string
    ): void {
      const state = xhrStateByRequest.get(this);
      if (state) {
        state.headers[name] = value;
      }
      return originalSetRequestHeader.call(this, name, value);
    };

    XMLHttpRequest.prototype.send = function send(
      body?: Document | XMLHttpRequestBodyInit | null
    ): void {
      const state = xhrStateByRequest.get(this);
      if (state) {
        if (typeof body === 'string') {
          state.requestBody = body;
        } else if (body instanceof URLSearchParams) {
          state.requestBody = body.toString();
        } else if (body == null) {
          state.requestBody = '';
        } else {
          state.requestBody = '[binary]';
        }

        if (!isRumDataplaneUrl(state.url)) {
          const startedAt = performance.now();
          const finalize = (): void => {
            this.removeEventListener('loadend', finalize);
            recordHttpEvent({
              transport: 'xhr',
              method: state.method,
              url: state.url,
              status: this.status,
              durationMs: Math.round(performance.now() - startedAt),
              requestHeaders: sanitizeHeaders(state.headers),
              responseHeaders: sanitizeHeaders(
                parseXhrResponseHeaders(this.getAllResponseHeaders())
              ),
              requestBody: serializeBody(state.requestBody),
              responseBody: serializeBody(
                this.responseText,
                this.getResponseHeader('content-type')
              ),
            });
          };
          this.addEventListener('loadend', finalize);
        }
      }

      return originalSend.call(this, body);
    };
  }
}
